/**
 * Token resolver — deep-clones a FormBlock and resolves template tokens.
 * Tokens: {ns}, {title}, {opts.*}, {params.*}, {item}
 */

import type { FormBlock, BlockSlot } from '../types/block';

export interface TokenContext {
  namespace?: string;
  title?: string;
  options?: Record<string, unknown>;
  params?: Record<string, unknown>;
}

export function resolveBlock(slot: BlockSlot, paramsId?: string): FormBlock {
  const cloned = deepCloneWithFunctions(slot.use) as FormBlock;

  const tokenCtx: TokenContext = {
    namespace: slot.namespace,
    title: slot.title,
    options: mergeBlockOptions(slot.use.options, slot.options),
    params: { ...slot.params, ...(paramsId != null ? { id: paramsId } : {}) },
  };

  return resolveTokensInObject(cloned, tokenCtx) as FormBlock;
}

const PRESERVE_BY_REFERENCE = new Set(['def', 'options', 'dataSource', 'schema']);

function deepCloneWithFunctions(obj: unknown): unknown {
  if (obj === null || typeof obj !== 'object') return obj;
  if (typeof obj === 'function') return obj;
  if (obj instanceof Date) return new Date(obj.getTime());
  if (obj instanceof RegExp) return new RegExp(obj.source, obj.flags);
  if (Array.isArray(obj)) return obj.map(deepCloneWithFunctions);

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (typeof value === 'function' || PRESERVE_BY_REFERENCE.has(key)) {
      result[key] = value;
    } else {
      result[key] = deepCloneWithFunctions(value);
    }
  }
  return result;
}

function mergeBlockOptions(
  blockOptions?: Record<string, { type: string; default: unknown }>,
  slotOptions?: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = {};

  if (blockOptions) {
    for (const [key, def] of Object.entries(blockOptions)) {
      merged[key] = slotOptions?.[key] ?? def.default;
    }
  }

  if (slotOptions) {
    for (const [key, val] of Object.entries(slotOptions)) {
      if (!(key in merged)) {
        merged[key] = val;
      }
    }
  }

  return merged;
}

function resolveTokensInObject(obj: unknown, ctx: TokenContext): unknown {
  if (typeof obj === 'string') {
    return resolveTokenString(obj, ctx);
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => resolveTokensInObject(item, ctx));
  }

  if (obj !== null && typeof obj === 'object') {
    if (obj instanceof Date || obj instanceof RegExp) return obj;
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const resolvedKey = resolveTokenString(key, ctx);
      if (PRESERVE_BY_REFERENCE.has(resolvedKey)) {
        result[resolvedKey] = value;
      } else {
        result[resolvedKey] = resolveTokensInObject(value, ctx);
      }
    }
    return result;
  }

  return obj;
}

function resolveTokenString(str: string, ctx: TokenContext): string {
  let result = str;

  if (ctx.namespace !== undefined) {
    result = result.replace(/\{ns\}/g, ctx.namespace);
  }

  if (ctx.title !== undefined) {
    result = result.replace(/\{title\}/g, ctx.title);
  }

  if (ctx.options) {
    result = result.replace(/\{opts\.([^}]+)\}/g, (_, key) => {
      const val = ctx.options?.[key];
      return val !== undefined ? String(val) : '';
    });
  }

  if (ctx.params) {
    result = result.replace(/\{params\.([^}]+)\}/g, (_, key) => {
      const val = ctx.params?.[key];
      return val !== undefined ? String(val) : '';
    });
  }

  return result;
}

/**
 * Resolves {ns} tokens in an array of form rules using the given namespace.
 * Used for schema-level `rules` which bypass the block token resolver.
 */
export function resolveRulesTokens<T>(rules: T[], namespace: string): T[] {
  if (!namespace) return rules;
  return resolveTokensInObject(rules, { namespace }) as T[];
}

function resolveTokenStringForItem(str: string, itemPath: string, rowIndex?: number): string {
  let result = str
    .replace(/\{item\.([^}]+)\}/g, (_, suffix: string) => `${itemPath}.${suffix}`)
    .replace(/\{item\}/g, itemPath);

  // {n} → 1-based row index (e.g., "Domain Name {n}" → "Domain Name 2" for index 1)
  if (rowIndex !== undefined) {
    result = result.replace(/\{n\}/g, String(rowIndex + 1));
  }

  return result;
}

export function resolveItemTokens(
  obj: unknown,
  itemPath: string,
  rowIndex?: number,
): unknown {
  if (typeof obj === 'string') {
    return resolveTokenStringForItem(obj, itemPath, rowIndex);
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => resolveItemTokens(item, itemPath, rowIndex));
  }

  if (obj !== null && typeof obj === 'object') {
    if (obj instanceof Date || obj instanceof RegExp) return obj;
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      if (PRESERVE_BY_REFERENCE.has(key)) {
        result[key] = value;
      } else {
        result[key] = resolveItemTokens(value, itemPath, rowIndex);
      }
    }
    return result;
  }

  return obj;
}
